/**
 * The room. Everything below `/r/:roomCode` renders inside this layout.
 *
 * It owns three things and nothing else:
 *
 *  1. **The socket lifecycle** — connect on mount, disconnect on unmount. The
 *     stored session token is replayed automatically (SPEC §5.4), so arriving at
 *     the URL a second time silently reclaims the seat.
 *  2. **The onboarding gate** — photo → name must be behind you before any room
 *     screen renders (SPEC §5.1, §6).
 *  3. **Phase following** — the server owns the phase; this navigates to match
 *     it. No screen ever has to redirect, and no screen can put the client into
 *     a phase the server is not in.
 */
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { isValidRoomCode } from '../lib/roomCode.js';
import { useGameStore } from '../store/gameStore.js';
import { useHasConnected, useRoomError } from '../store/index.js';
import { ClaimSeatScreen } from '../screens/join/ClaimSeatScreen.js';
import { Body, Button, Screen, ScreenTitle, Spinner } from './ui.js';

/** Sub-routes that are part of onboarding rather than a game phase. */
export const ONBOARDING_PATHS = ['photo', 'name'] as const;

export function RoomRoute() {
  const params = useParams<{ roomCode: string }>();
  const roomCode = (params.roomCode ?? '').toUpperCase();

  const connect = useGameStore((s) => s.connect);
  const disconnect = useGameStore((s) => s.disconnect);
  const valid = isValidRoomCode(roomCode);

  useEffect(() => {
    if (!valid) return;
    connect(roomCode);
    return () => disconnect();
  }, [valid, roomCode, connect, disconnect]);

  if (!valid) return <Navigate to="/join" replace />;

  return (
    <>
      <PhaseSync roomCode={roomCode} />
      <RoomGate />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Gate                                                                        */
/* -------------------------------------------------------------------------- */

/** Blocks the outlet until there is something real to render. */
function RoomGate() {
  const snapshot = useGameStore((s) => s.snapshot);
  const everConnected = useHasConnected();
  const error = useRoomError();
  const status = useGameStore((s) => s.status);
  const offeredSeats = useGameStore((s) => s.claimableSeats !== null);

  if (error?.fatal) return <FatalError message={error.message} />;

  /*
    The room has seats going spare and does not recognise this device, so it is
    asking which one we are (SPEC §5.4). This sits ahead of the snapshot check
    because there *is* no snapshot yet — the offer arrives instead of a welcome,
    and answering it is what produces one.
  */
  if (offeredSeats) return <ClaimSeatScreen />;

  if (!snapshot) {
    if (status === 'closed' && !everConnected) {
      return <FatalError message="We couldn't reach that room. Check the code and try again." />;
    }
    return (
      <Screen>
        <Spinner label="Joining" />
      </Screen>
    );
  }

  return <Outlet />;
}

function FatalError({ message }: { message: string }) {
  const leaveRoom = useGameStore((s) => s.leaveRoom);
  return (
    <Screen>
      <ScreenTitle kicker="Room">No luck</ScreenTitle>
      <Body>{message}</Body>
      <div className="flex-1" />
      <Button
        onClick={() => {
          leaveRoom();
          window.location.assign('/');
        }}
      >
        Start over
      </Button>
    </Screen>
  );
}

/* -------------------------------------------------------------------------- */
/* Phase following                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Keeps the URL on the route the server's phase says it should be on.
 *
 * Onboarding wins while it is incomplete: a player joining mid-lobby is not
 * dragged into the lobby before they have taken a photo. Once the
 * seat has a name and a photo, the phase is in charge — including dragging
 * everyone out of the lobby the instant the host starts the game.
 */
function PhaseSync({ roomCode }: { roomCode: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const phase = useGameStore((s) => s.snapshot?.phase ?? null);
  const profileSubmitted = useGameStore((s) => s.profileSubmitted);
  const hasSeat = useGameStore((s) => s.selfId !== null);

  useEffect(() => {
    if (!phase || !hasSeat) return;

    const base = `/r/${roomCode}`;
    const segment = location.pathname.startsWith(`${base}/`)
      ? (location.pathname.slice(base.length + 1).split('/')[0] ?? '')
      : '';

    if (!profileSubmitted) {
      // Any of the onboarding steps is fine; anything else is not.
      if ((ONBOARDING_PATHS as readonly string[]).includes(segment)) return;
      navigate(`${base}/photo`, { replace: true });
      return;
    }

    /**
     * Out of onboarding, the phase is the whole answer — including in the
     * lobby, which now carries the QR and the room code itself. There used to
     * be a second `/invite` screen the host was routed to instead; it showed
     * the same QR, the same code and a head count, and the only way back was a
     * button. One screen for "people are arriving" is enough.
     */
    const target = phase;
    if (segment === target) return;

    navigate(`${base}/${target}`, { replace: true });
  }, [phase, profileSubmitted, hasSeat, roomCode, location.pathname, navigate]);

  return null;
}
